import { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { authAPI } from '../services/api';
import { useQueryClient } from '@tanstack/react-query';
import { Capacitor } from '@capacitor/core';
// GoogleAuth accessed dynamically via Capacitor.Plugins to avoid iOS crash
// when GoogleService-Info.plist is missing
import pushNotificationService from '../services/pushNotifications';
import { logSignup } from '../utils/facebookEvents';
import { ga } from '../services/ga';

const AuthContext = createContext(null);

// Google Client ID from environment
const GOOGLE_CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID;

export function AuthProvider({ children }) {
  const queryClient = useQueryClient();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [googleLoaded, setGoogleLoaded] = useState(false);

  // Load Google Sign-In script
  useEffect(() => {
    if (!GOOGLE_CLIENT_ID) return;

    // Check if script already exists
    if (document.querySelector('script[src="https://accounts.google.com/gsi/client"]')) {
      setGoogleLoaded(true);
      return;
    }

    const script = document.createElement('script');
    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true;
    script.defer = true;
    script.onload = () => setGoogleLoaded(true);
    document.body.appendChild(script);
  }, []);

  // Initialize auth state from localStorage
  useEffect(() => {
    const token = localStorage.getItem('token');
    const storedUser = localStorage.getItem('user');

    if (token && storedUser) {
      try {
        const parsedUser = JSON.parse(storedUser);
        setUser(parsedUser);
        // Sync theme from user profile
        if (parsedUser.theme) {
          localStorage.setItem('theme', parsedUser.theme);
        }
        // Sync swipeEffect from user profile
        if (parsedUser.swipeEffect !== undefined) {
          localStorage.setItem('swipeEffect', String(parsedUser.swipeEffect));
        }
        // Initialize push notifications on session restore
        setTimeout(() => pushNotificationService.initialize(), 1000);
      } catch {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
      }
    }
    setLoading(false);
  }, []);

  const login = useCallback(async ({ email, phone, password }) => {
    try {
      setError(null);
      const platform = Capacitor.isNativePlatform() ? Capacitor.getPlatform() : 'web';
      const { data } = await authAPI.login({ email, phone, password, platform });
      const userData = data.data.user;
      localStorage.setItem('token', data.data.token);
      localStorage.setItem('user', JSON.stringify(userData));

      // Store encryption key in localStorage for API requests
      if (data.data.encryptionKey) {
        localStorage.setItem('encryptionKey', data.data.encryptionKey);
      }

      // Sync theme from user profile
      if (userData.theme) {
        localStorage.setItem('theme', userData.theme);
        // Trigger theme application
        window.dispatchEvent(new CustomEvent('theme-change', { detail: userData.theme }));
      }
      // Sync swipeEffect from user profile
      if (userData.swipeEffect !== undefined) {
        localStorage.setItem('swipeEffect', String(userData.swipeEffect));
      }
      // Sync config from user profile (country-specific settings)
      if (userData.config) {
        localStorage.setItem('appConfig', JSON.stringify(userData.config));
        // Dispatch event to notify ConfigContext to reload the config
        window.dispatchEvent(new Event('configUpdated'));
      }
      // Track login method for logout handling
      localStorage.setItem('loginMethod', 'password');
      setUser(userData);
      ga.login('email');

      // Initialize push notifications after login
      setTimeout(() => pushNotificationService.initialize(), 1000);

      return userData;
    } catch (err) {
      const errorMessage = err.error || 'Login failed';
      setError(errorMessage);
      throw new Error(errorMessage);
    }
  }, []);

  const googleLogin = useCallback(async (credential) => {
    try {
      setError(null);
      // Check for stored referral code and UTM (from ?ref= / ?utm_source= URL before OAuth redirect)
      const pendingReferralCode = localStorage.getItem('pendingReferralCode');
      const storedUtm = localStorage.getItem('pendingUtm');
      const utm = storedUtm ? JSON.parse(storedUtm) : undefined;
      const platform = Capacitor.isNativePlatform() ? Capacitor.getPlatform() : 'web';
      const { data } = await authAPI.googleAuth({
        credential,
        referralCode: pendingReferralCode || undefined,
        platform,
        utm
      });
      if (pendingReferralCode) {
        localStorage.removeItem('pendingReferralCode');
      }
      localStorage.removeItem('pendingUtm');
      const userData = data.data.user;
      localStorage.setItem('token', data.data.token);
      localStorage.setItem('user', JSON.stringify(userData));

      // Store encryption key in localStorage for API requests
      if (data.data.encryptionKey) {
        localStorage.setItem('encryptionKey', data.data.encryptionKey);
      }

      // Sync theme from user profile
      if (userData.theme) {
        localStorage.setItem('theme', userData.theme);
        window.dispatchEvent(new CustomEvent('theme-change', { detail: userData.theme }));
      }
      // Sync swipeEffect from user profile
      if (userData.swipeEffect !== undefined) {
        localStorage.setItem('swipeEffect', String(userData.swipeEffect));
      }
      // Sync config from user profile (country-specific settings)
      if (userData.config) {
        localStorage.setItem('appConfig', JSON.stringify(userData.config));
        // Dispatch event to notify ConfigContext to reload the config
        window.dispatchEvent(new Event('configUpdated'));
      }
      // Track login method for logout handling
      localStorage.setItem('loginMethod', 'google');
      setUser(userData);

      // Log signup event for new Google users
      if (data.data.isNewUser) {
        logSignup('google');
        ga.signup('google');
        ga.trialStart();
      } else {
        ga.login('google');
      }

      // Initialize push notifications after login
      setTimeout(() => pushNotificationService.initialize(), 1000);

      return { user: userData, isNewUser: data.data.isNewUser };
    } catch (err) {
      const errorMessage = err.error || 'Google login failed';
      setError(errorMessage);
      throw new Error(errorMessage);
    }
  }, []);

  const appleLogin = useCallback(async ({ identityToken, fullName }) => {
    try {
      setError(null);
      const pendingReferralCode = localStorage.getItem('pendingReferralCode');
      const storedUtm = localStorage.getItem('pendingUtm');
      const utm = storedUtm ? JSON.parse(storedUtm) : undefined;
      const platform = Capacitor.isNativePlatform() ? Capacitor.getPlatform() : 'web';
      const { data } = await authAPI.appleAuth({
        identityToken,
        fullName,
        referralCode: pendingReferralCode || undefined,
        platform,
        utm
      });
      if (pendingReferralCode) {
        localStorage.removeItem('pendingReferralCode');
      }
      localStorage.removeItem('pendingUtm');
      const userData = data.data.user;
      localStorage.setItem('token', data.data.token);
      localStorage.setItem('user', JSON.stringify(userData));

      if (data.data.encryptionKey) {
        localStorage.setItem('encryptionKey', data.data.encryptionKey);
      }

      if (userData.theme) {
        localStorage.setItem('theme', userData.theme);
        window.dispatchEvent(new CustomEvent('theme-change', { detail: userData.theme }));
      }
      if (userData.swipeEffect !== undefined) {
        localStorage.setItem('swipeEffect', String(userData.swipeEffect));
      }
      if (userData.config) {
        localStorage.setItem('appConfig', JSON.stringify(userData.config));
        window.dispatchEvent(new Event('configUpdated'));
      }
      localStorage.setItem('loginMethod', 'apple');
      setUser(userData);

      if (data.data.isNewUser) {
        logSignup('apple');
        ga.signup('apple');
        ga.trialStart();
      } else {
        ga.login('apple');
      }

      setTimeout(() => pushNotificationService.initialize(), 1000);

      return { user: userData, isNewUser: data.data.isNewUser };
    } catch (err) {
      const errorMessage = err.error || 'Apple login failed';
      setError(errorMessage);
      throw new Error(errorMessage);
    }
  }, []);

  const setPassword = useCallback(async (password, confirmPassword) => {
    try {
      setError(null);
      const { data } = await authAPI.setPassword({ password, confirmPassword });
      const userData = data.data.user;

      // Update user in state and localStorage
      const updatedUser = { ...user, ...userData, needsPasswordSetup: false };
      localStorage.setItem('user', JSON.stringify(updatedUser));
      localStorage.setItem('token', data.data.token);
      setUser(updatedUser);

      return updatedUser;
    } catch (err) {
      const errorMessage = err.error || 'Failed to set password';
      setError(errorMessage);
      throw new Error(errorMessage);
    }
  }, [user]);

  const skipPasswordSetup = useCallback(async () => {
    try {
      await authAPI.skipPasswordSetup();
      // User can continue without setting password
      // They'll be reminded later
    } catch (err) {
      console.error('Failed to skip password setup:', err);
    }
  }, []);

  const register = useCallback(async (userData) => {
    try {
      setError(null);
      // Detect platform for registration tracking
      const platform = Capacitor.isNativePlatform() ? Capacitor.getPlatform() : 'web';
      const { data } = await authAPI.register({ ...userData, platform });
      localStorage.setItem('token', data.data.token);
      localStorage.setItem('user', JSON.stringify(data.data.user));

      // Store encryption key in localStorage (auto-generated during registration)
      if (data.data.encryptionKey) {
        localStorage.setItem('encryptionKey', data.data.encryptionKey);
      }

      // Store country-specific config in localStorage for ConfigContext
      if (data.data.user.config) {
        localStorage.setItem('appConfig', JSON.stringify(data.data.user.config));
        // Dispatch event to notify ConfigContext to reload the config
        window.dispatchEvent(new Event('configUpdated'));
      }

      setUser(data.data.user);
      logSignup('email');
      ga.signup('email');
      ga.trialStart();
      return data.data.user;
    } catch (err) {
      // err.error already contains formatted message from api.js interceptor
      // For validation errors, it contains "field: message" format joined by newlines
      const errorMessage = err.error || 'Registration failed';
      setError(errorMessage);
      throw new Error(errorMessage);
    }
  }, []);

  const logout = useCallback(() => {
    // Unregister push notifications before clearing storage
    pushNotificationService.unregister();

    // Check login method before clearing storage
    const loginMethod = localStorage.getItem('loginMethod');

    // Preserve push device ID across logout/login
    const pushDeviceId = localStorage.getItem('push_device_id');

    // Clear React Query in-memory + persisted cache
    queryClient.clear();
    localStorage.removeItem('hj-query-cache');

    // Clear ALL localStorage items for clean slate on new login
    localStorage.clear();

    // Restore push device ID so re-login replaces the same token
    if (pushDeviceId) {
      localStorage.setItem('push_device_id', pushDeviceId);
    }

    // Clear sessionStorage
    sessionStorage.clear();

    // Clear all cookies
    document.cookie.split(';').forEach(cookie => {
      const name = cookie.split('=')[0].trim();
      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
    });

    setUser(null);

    // Fire backend logout in background (non-blocking, not critical)
    authAPI.logout().catch(() => {});

    // Sign out from Google in background (non-blocking, fire-and-forget)
    if (loginMethod === 'google' && Capacitor.isNativePlatform()) {
      try {
        const GoogleAuth = window.Capacitor?.Plugins?.GoogleAuth;
        if (GoogleAuth) GoogleAuth.signOut().catch(() => {});
      } catch (e) {
        // Ignore any sync errors from GoogleAuth
      }
    }

    // Reset RevenueCat on iOS to clear user association
    if (Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'ios') {
      import('../services/appleIAP').then(({ resetAppleIAP }) => {
        resetAppleIAP().catch(() => {});
      }).catch(() => {});
    }

    // On native, avoid full page reload (causes crash on iOS).
    // ProtectedRoute will redirect to /login when user is null.
    // On web, do a hard redirect for a clean slate.
    if (!Capacitor.isNativePlatform()) {
      window.location.href = '/login';
    }
  }, []);

  const deleteAccount = useCallback(async (password) => {
    try {
      setError(null);
      await authAPI.deleteAccount(password);

      // Clear ALL localStorage items
      localStorage.clear();

      // Clear sessionStorage
      sessionStorage.clear();

      // Clear all cookies
      document.cookie.split(';').forEach(cookie => {
        const name = cookie.split('=')[0].trim();
        document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
      });

      // Reset RevenueCat on iOS
      if (Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'ios') {
        import('../services/appleIAP').then(({ resetAppleIAP }) => {
          resetAppleIAP().catch(() => {});
        }).catch(() => {});
      }

      setUser(null);
      // On native, avoid full page reload (causes crash on iOS)
      if (!Capacitor.isNativePlatform()) {
        window.location.href = '/login';
      }
    } catch (err) {
      const errorMessage = err.error || 'Failed to delete account';
      setError(errorMessage);
      throw new Error(errorMessage);
    }
  }, []);

  const updateProfile = useCallback(async (profileData) => {
    try {
      setError(null);
      const { data } = await authAPI.updateProfile(profileData);
      const updatedUser = data.data.user;
      localStorage.setItem('user', JSON.stringify(updatedUser));
      setUser(updatedUser);
      return updatedUser;
    } catch (err) {
      const errorMessage = err.error || 'Profile update failed';
      setError(errorMessage);
      throw new Error(errorMessage);
    }
  }, []);

  const refreshUser = useCallback(async () => {
    try {
      const { data } = await authAPI.getProfile();
      const updatedUser = data.data.user;
      localStorage.setItem('user', JSON.stringify(updatedUser));

      // Update encryption key if returned
      if (data.data.encryptionKey) {
        localStorage.setItem('encryptionKey', data.data.encryptionKey);
      }

      setUser(updatedUser);
      return updatedUser;
    } catch (err) {
      console.error('Failed to refresh user:', err);
    }
  }, []);

  // Check if encryption key exists (either from user data or localStorage)
  // This ensures immediate availability on page refresh
  // Memoized to prevent unnecessary re-renders and query re-runs
  const hasEncryptionKey = useMemo(() => {
    return user?.hasEncryptionKey || !!localStorage.getItem('encryptionKey');
  }, [user?.hasEncryptionKey]);

  // Check if user needs to set up password (social auth users)
  const needsPasswordSetup = user?.needsPasswordSetup || false;

  const value = {
    user,
    loading,
    error,
    isAuthenticated: !!user,
    hasEncryptionKey,
    needsPasswordSetup,
    googleLoaded,
    googleClientId: GOOGLE_CLIENT_ID,
    login,
    googleLogin,
    appleLogin,
    setPassword,
    skipPasswordSetup,
    register,
    logout,
    deleteAccount,
    updateProfile,
    refreshUser,
    clearError: () => setError(null)
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
